import { supabase } from '../lib/supabase'
import { logActivity } from '../utils/activityLogger'

export interface JobGenerationResult {
  job_id: string
  scheduled_date: string
}

export interface JobGenerationPreviewResult {
  job_id: string | null
  scheduled_date: string
  visit_group_id: string | null
  visit_group_name: string | null
  is_preview: boolean
  /** 1 if this row would create a new job; 0 otherwise (from RPC when available). */
  created_delta?: number
  /** 1 if this row is an existing slot (already has a job); 0 otherwise. */
  skipped_delta?: number
}

export interface NightlyGenerationResult {
  contract_id: string
  jobs_created: number
  jobs_skipped: number
  error_message: string | null
}

export interface RecurringWindowResult {
  contract_id: string
  jobs_created: number
  jobs_skipped: number
  error_message: string | null
}

/**
 * Generate jobs for a specific contract
 * @param contractId - The contract UUID
 * @param startDate - Optional start date (defaults to today)
 * @param endDate - Optional end date (defaults to 90 days from start)
 * @returns Array of generated job IDs and scheduled dates
 */
export async function generateJobsForContract(
  contractId: string,
  startDate?: Date,
  endDate?: Date
): Promise<JobGenerationResult[]> {
  try {
    const start = startDate ? startDate.toISOString().split('T')[0] : null
    const end = endDate ? endDate.toISOString().split('T')[0] : null

    const { data, error } = await supabase.rpc('generate_jobs_for_contract_range', {
      contract_uuid: contractId,
      start_date_param: start,
      end_date_param: end,
      dry_run_param: false,
    })

    if (error) throw error

    const results: JobGenerationResult[] = (data || [])
      .filter((row: any) => !!row.job_id)
      .map((row: any) => ({
        job_id: row.job_id,
        scheduled_date: row.scheduled_date,
      }))

    if (results.length > 0) {
      await logActivity({
        activity_type: 'job_auto_created',
        entity_type: 'contract',
        entity_id: contractId,
        description: `${results.length} job(s) generated from contract via manual action`,
        metadata: {
          jobs_created: results.length,
          created_by: 'manual_generate',
          job_ids: results.map((r: JobGenerationResult) => r.job_id),
        },
      })
    }

    return results
  } catch (error) {
    console.error('Error generating jobs for contract:', error)
    throw error
  }
}

export async function previewJobsForContractRange(
  contractId: string,
  startDate?: Date,
  endDate?: Date
): Promise<JobGenerationPreviewResult[]> {
  try {
    const start = startDate ? startDate.toISOString().split('T')[0] : null
    const end = endDate ? endDate.toISOString().split('T')[0] : null

    const { data, error } = await supabase.rpc('generate_jobs_for_contract_range', {
      contract_uuid: contractId,
      start_date_param: start,
      end_date_param: end,
      dry_run_param: true,
    })

    if (error) throw error

    return (data || []).map((row: any) => ({
      job_id: row.job_id || null,
      scheduled_date: row.scheduled_date,
      visit_group_id: row.visit_group_id || null,
      visit_group_name: row.visit_group_name || null,
      is_preview: !!row.is_preview,
      created_delta: row.created_delta != null ? Number(row.created_delta) : undefined,
      skipped_delta: row.skipped_delta != null ? Number(row.skipped_delta) : undefined,
    }))
  } catch (error) {
    console.error('Error previewing jobs for contract range:', error)
    throw error
  }
}

/**
 * Trigger nightly job generation for all active recurring contracts
 * This should be called by a cron job, but can also be called manually for testing
 * @returns Array of results for each contract processed
 */
export async function generateRecurringJobsNightly(): Promise<NightlyGenerationResult[]> {
  try {
    const { data, error } = await supabase.rpc('generate_recurring_jobs_nightly')

    if (error) throw error

    const results: NightlyGenerationResult[] = (data || []).map((row: any) => ({
      contract_id: row.contract_id,
      jobs_created: row.jobs_created || 0,
      jobs_skipped: row.jobs_skipped || 0,
      error_message: row.error_message || null,
    }))

    const totalCreated = results.reduce((sum: number, r: NightlyGenerationResult) => sum + r.jobs_created, 0)
    if (totalCreated > 0) {
      for (const r of results) {
        if (r.jobs_created > 0) {
          await logActivity({
            activity_type: 'job_auto_created',
            entity_type: 'contract',
            entity_id: r.contract_id,
            description: `${r.jobs_created} job(s) auto-created by nightly recurring generation`,
            metadata: {
              jobs_created: r.jobs_created,
              jobs_skipped: r.jobs_skipped,
              created_by: 'nightly_auto_create',
            },
          })
        }
      }
    }

    return results
  } catch (error) {
    console.error('Error in nightly job generation:', error)
    throw error
  }
}

export async function runRecurringJobsWindow(
  startDate?: Date,
  endDate?: Date,
  dryRun: boolean = false
): Promise<RecurringWindowResult[]> {
  try {
    const start = startDate ? startDate.toISOString().split('T')[0] : null
    const end = endDate ? endDate.toISOString().split('T')[0] : null

    const { data, error } = await supabase.rpc('generate_recurring_jobs_for_window', {
      start_date_param: start,
      end_date_param: end,
      dry_run_param: dryRun,
    })

    if (error) throw error

    const results: RecurringWindowResult[] = (data || []).map((row: any) => ({
      contract_id: row.contract_id,
      jobs_created: row.jobs_created || 0,
      jobs_skipped: row.jobs_skipped || 0,
      error_message: row.error_message || null,
    }))

    if (!dryRun) {
      for (const r of results) {
        if (r.jobs_created > 0) {
          await logActivity({
            activity_type: 'job_auto_created',
            entity_type: 'contract',
            entity_id: r.contract_id,
            description: `${r.jobs_created} job(s) auto-created by recurring window generation`,
            metadata: {
              jobs_created: r.jobs_created,
              jobs_skipped: r.jobs_skipped,
              created_by: 'recurring_window',
            },
          })
        }
      }
    }

    return results
  } catch (error) {
    console.error('Error running recurring jobs window:', error)
    throw error
  }
}

